import React, { lazy } from 'react';

import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEventsOutlined';
import FolderSharedIcon from '@mui/icons-material/FolderSharedOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import SummarizeIcon from '@mui/icons-material/SummarizeOutlined';
import GroupAddIcon from '@mui/icons-material/GroupAddOutlined';

import Breadcrumb from '@common/components/breadcrumb/breadcrumb';
import { BLANK_PAGE_ROUTE } from '@routes';
import {
  APPROVE_STUDENT_ACCESS_LISTS,
  CONFIGURE_SETTINGS,
  CREATE_JOURNAL_ACTIONS,
  CREATE_MONITOR_GROUPS,
  CREATE_STUDENT_FORMS,
  MANAGE_STUDENT_SUPPORTS
} from '@rights';
import V2SettingsIframe from '@aware/common/v2-settings-iframe';
import { StudentPortfoliosBundle } from '@aware/bundles';
import { V2_ROUTE_PAGES } from '@aware/v2-routes/constants';
import NewStudentForm from '@aware/common/v2-settings/new-student-form';
import ApproveStudentAccessLists from '@aware/common/v2-settings/student-access-lists/approve-student-access-lists';
import CreateStudentAccessList from '@aware/common/v2-settings/student-access-lists/create-student-access-list';
import ManageMyStudentAccessLists from '@aware/common/v2-settings/student-access-lists/manage-my-student-access-lists';
import AssignFormToStudents from '@aware/common/v2-settings/assign-form-to-students';
import V2ReportsPage from '@aware/common/v2-reports-page/v2-reports-page';

const V2StudentReportsPage = () => (
  <V2ReportsPage tabId="Forms" baseRoute={V2_ROUTE_PAGES.studentReports} />
);

const STUDENTS_ROUTE_CONFIG = {
  title: 'Students',
  settings: [
    {
      title: 'Monitor Groups',
      order: 1,
      paths: [
        {
          path: `${V2_ROUTE_PAGES.studentSettings}/new-monitor-group`,
          title: 'New Monitor Group',
          rights: [{ right: [CREATE_MONITOR_GROUPS] }],
          exact: true,
          component: () => (
            <V2SettingsIframe
              iframeSrc="/aware/?embed=1&leftSrc=/aware/manage/monitorgrouplist.aspx&detailsSrc=/aware/manage/monitorgroupdetails.aspx"
              title="New Monitor Groups"
            />
          ),
          // NOTE: Remove this check when we remove feature flag for Monitor Groups V2.
          // Only show this in nav when flag is off, otherwise new nav item will appear
          featureFlagCheck: configuration =>
            configuration.awareMonitorGroupsV2Updated === 'False'
        },
        {
          path: `${V2_ROUTE_PAGES.studentSettings}/manage-monitor-groups`,
          title: 'Manage Monitor Groups',
          rights: [{ right: [CREATE_MONITOR_GROUPS] }],
          exact: true,
          component: () => (
            <V2SettingsIframe
              iframeSrc={`/aware/?embed=1&leftSrc=/aware/manage/monitorgrouplist.aspx&detailsSrc=${BLANK_PAGE_ROUTE}`}
              title="Manage Monitor Groups"
            />
          ),
          // NOTE: Remove this check when we remove feature flag for Monitor Groups V2.
          // Only show this in nav when flag is off, otherwise manage monitor groups
          // is included in the new monitor groups feature for V2.
          featureFlagCheck: configuration =>
            configuration.awareMonitorGroupsV2Updated === 'False'
        }
      ]
    },
    {
      title: 'Student Access Lists',
      order: 2,
      paths: [
        {
          path: `${V2_ROUTE_PAGES.studentSettings}/manage-my-student-access-lists`,
          title: 'Manage My Student Access Lists',
          exact: true,
          component: ManageMyStudentAccessLists
        },
        {
          path: `${V2_ROUTE_PAGES.studentSettings}/create-student-access-list`,
          title: 'Create Student Access List',
          exact: true,
          component: () => <CreateStudentAccessList />
        },
        {
          path: `${V2_ROUTE_PAGES.studentSettings}/approve-student-access-lists`,
          title: 'Approve Student Access Lists',
          rights: [
            {
              right: [APPROVE_STUDENT_ACCESS_LISTS]
            }
          ],
          exact: true,
          component: ApproveStudentAccessLists
        }
      ]
    },
    {
      title: 'Students',
      order: 3,
      paths: [
        {
          path: `${V2_ROUTE_PAGES.studentSettings}/share-my-classes`,
          title: 'Share My Classes',
          permissions: ['assessments.navigation.administer'],
          exact: true,
          order: 0,
          component: () => (
            <V2SettingsIframe
              iframeSrc="/aware/?embed=1&detailsSrc=/aware/Students/MonitorLists/ShareClassesToMonitorList.aspx"
              title="Share My Classes"
            />
          )
        },
        {
          path: `${V2_ROUTE_PAGES.studentSettings}/student-journal-actions`,
          title: 'Student Journal Actions',
          rights: [
            {
              right: [CREATE_JOURNAL_ACTIONS]
            }
          ],
          exact: true,
          order: 0,
          component: () => (
            <V2SettingsIframe
              iframeSrc={`/aware/?embed=1&leftSrc=/aware/manage/interventionactionlist.aspx&detailsSrc=${BLANK_PAGE_ROUTE}`}
              title="Student Journal Actions"
            />
          ),
          texasOnly: true
        },
        {
          path: `${V2_ROUTE_PAGES.studentSettings}/new-journal-action`,
          title: 'New Journal Action',
          rights: [
            {
              right: [CREATE_JOURNAL_ACTIONS]
            }
          ],
          exact: true,
          order: 0,
          component: () => (
            <V2SettingsIframe
              iframeSrc="/aware/?embed=1&leftSrc=/aware/manage/interventionactionlist.aspx&detailsSrc=/aware/manage/interventionactiondetails.aspx"
              title="New Student Action"
            />
          ),
          texasOnly: true
        },
        {
          path: `${V2_ROUTE_PAGES.studentSettings}/upload-student-supports`,
          title: 'Upload Student Supports',
          rights: [
            {
              right: [MANAGE_STUDENT_SUPPORTS]
            }
          ],
          exact: true,
          order: 0,
          component: () => (
            <V2SettingsIframe
              iframeSrc="/aware/?embed=1&detailsSrc=/aware/manage/UploadStudentSupports.aspx"
              title="Upload Student Supports"
            />
          )
        },
        {
          path: `${V2_ROUTE_PAGES.studentSettings}/manage-student-supports`,
          title: 'Manage Student Supports',
          rights: [
            {
              right: [MANAGE_STUDENT_SUPPORTS]
            }
          ],
          exact: true,
          order: 1,
          component: lazy(
            () =>
              import(
                /* webpackChunkName: "aware-manage-student-supports" */ '@aware/pages/v2-student-supports/student-supports'
              )
          )
        },
        {
          path: `${V2_ROUTE_PAGES.studentSettings}/mastery-config`,
          title: 'Configure Mastery Thresholds',
          rights: [
            {
              right: [CONFIGURE_SETTINGS]
            }
          ],
          exact: true,
          order: 0,
          component: lazy(
            () =>
              import(
                /* webpackChunkName: "aware-overview-mastery-config" */ '@aware/pages/v2-student-mastery/mastery-thresholds/mastery-config-view'
              )
          )
        }
      ]
    },
    {
      title: 'Forms',
      order: 4,
      paths: [
        {
          path: `${V2_ROUTE_PAGES.studentSettings}/configure-form-categories`,
          title: 'Configure Form Categories',
          rights: [
            {
              right: [CREATE_STUDENT_FORMS]
            }
          ],
          exact: true,
          component: () => (
            <V2SettingsIframe
              iframeSrc="/aware/?embed=1&detailsSrc=/aware/Manage/Forms/FormCategories.aspx"
              title="Configure Form Categories"
            />
          ),
          texasOnly: true
        },
        {
          path: `${V2_ROUTE_PAGES.studentSettings}/new-student-form`,
          title: 'New Student Form',
          rights: [
            {
              right: [CREATE_STUDENT_FORMS]
            }
          ],
          exact: true,
          component: NewStudentForm,
          texasOnly: true
        },
        {
          path: `${V2_ROUTE_PAGES.studentSettings}/assign-form-to-students`,
          title: 'Assign Form to Students',
          rights: [
            {
              right: [CREATE_STUDENT_FORMS]
            }
          ],
          exact: true,
          component: AssignFormToStudents,
          texasOnly: true
        },
        {
          path: `${V2_ROUTE_PAGES.studentSettings}/browse-community-forms`,
          title: 'Browse Community Forms',
          rights: [
            {
              right: [CREATE_STUDENT_FORMS]
            }
          ],
          exact: true,
          component: () => (
            <V2SettingsIframe
              iframeSrc={`/aware/?embed=1&leftSrc=/aware/community/communitytree.aspx&detailsSrc=${BLANK_PAGE_ROUTE}`}
              title="Browse Community Forms"
            />
          ),
          texasOnly: true
        }
      ]
    }
  ],
  profiles: {
    path: V2_ROUTE_PAGES.studentProfiles,
    icon: <PersonIcon />,
    title: 'Profiles',
    breadcrumb: () => <Breadcrumb title="Profiles" to={V2_ROUTE_PAGES.studentProfiles} />,
    order: 1,
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "aware-student-profiles" */ '@aware/pages/v2-student-profiles/student-profiles'
        )
    )
  },
  studentPortfolios: {
    path: V2_ROUTE_PAGES.studentPortfolios,
    icon: <FolderSharedIcon />,
    title: 'Portfolios',
    breadcrumb: () => (
      <Breadcrumb title="Portfolios" to={V2_ROUTE_PAGES.studentPortfolios} />
    ),
    order: 2,
    exact: true,
    component: StudentPortfoliosBundle,
    routes: [
      {
        path: `/:area/:page/:id/entries/export`,
        exact: true,
        component: () => <StudentPortfoliosBundle exportEntries />
      },
      {
        path: `/:area/:page/:id/:viewId?/:subviewId?`,
        breadcrumb: null,
        exact: true,
        component: ({ match }) =>
          match.params.subviewId === 'export' ? null : (
            <StudentPortfoliosBundle showDetails />
          )
      }
    ],
    texasOnly: true
  },
  studentMastery: {
    path: V2_ROUTE_PAGES.studentMastery,
    icon: <EmojiEventsIcon />,
    title: 'Mastery',
    breadcrumb: () => <Breadcrumb title="Mastery" to={V2_ROUTE_PAGES.studentMastery} />,
    order: 3,
    routes: [
      {
        path: `${V2_ROUTE_PAGES.studentMastery}/:id?`, // Render component
        component: lazy(
          () =>
            import(
              /* webpackChunkName: "aware-student-mastery" */ '@aware/pages/v2-student-mastery/mastery'
            )
        )
      }
    ]
  },
  forms: {
    path: V2_ROUTE_PAGES.studentForms,
    icon: <DescriptionIcon />,
    title: 'Forms',
    breadcrumb: () => <Breadcrumb title="Forms" to={V2_ROUTE_PAGES.studentForms} />,
    order: 4,
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "aware-student-forms" */ '@aware/pages/v2-student-forms/student-forms'
        )
    ),
    texasOnly: true
  },
  reports: {
    path: V2_ROUTE_PAGES.studentReports,
    icon: <SummarizeIcon />,
    title: 'Reports',
    collapsedTitle: 'Forms Reports',
    breadcrumb: () => <Breadcrumb title="Reports" to={V2_ROUTE_PAGES.studentReports} />,
    order: 4,
    exact: true,
    component: V2StudentReportsPage,
    routes: [
      {
        path: `${V2_ROUTE_PAGES.studentReports}/:reportId`,
        component: V2StudentReportsPage
      }
    ]
  },
  studentMonitorGroups: {
    path: V2_ROUTE_PAGES.studentMonitorGroups,
    icon: <GroupAddIcon />,
    title: 'Monitor Groups',
    breadcrumb: () => (
      <Breadcrumb title="Monitor Groups" to={V2_ROUTE_PAGES.studentMonitorGroups} />
    ),
    order: 5,
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "aware-student-monitor-groups" */ '@aware/pages/v2-student-monitor-groups/monitor-groups'
        )
    )
  }
};

export default STUDENTS_ROUTE_CONFIG;
